import React, { useEffect, useState } from "react";
import { Flame, Trophy, Swords, Check, X, Users, Share2 } from "lucide-react";
import api from "@/lib/api";
import { Poster, gross } from "@/components/Movies";
import { ShareCardModal } from "@/components/ShareCardModal";
import { FaceOffLeaderboard } from "@/components/FaceOffLeaderboard";
import { getPlayer } from "@/lib/player";
import { getQuizState, getTodayResult, recordQuizResult, currentStreak } from "@/lib/quizStorage";

const KEY = "ot_movie_quiz_v1";
const todayKey = () => new Date().toISOString().slice(0, 10);
const shareCardFor = (result, streak) => {
  const n = result.results.length;
  const winners = result.results.map((r) => { const w = r.a.id === r.winner ? r.a : r.b, l = w === r.a ? r.b : r.a; return `${w.title} beat ${l.title}`; });
  return {
    badge: "Box Office Face-Off", greeting: result.correct === n ? "Clean sweep — I scored" : "Today I scored", title: `${result.correct}/${n}`,
    body: `${winners.join(" · ")}.${streak > 1 ? ` ${streak}-day streak.` : ""}`,
    dateLabel: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }), footer: "Can you beat me? → oddlytrue.co/movies",
  };
};

const Contender = ({ m, side, onPick, result }) => {
  const won = result && result.winner === m.id;
  const picked = result && result.pick === m.id;
  return (
    <button type="button" disabled={!!result} onClick={() => onPick(m.id)} data-testid={`faceoff-pick-${side}`}
      className={`group flex flex-col items-center text-center p-3 rounded-xl border-2 transition-colors ${result ? (won ? "border-gold bg-gold/10" : "border-ink/10 bg-white opacity-70") : "border-ink/15 bg-white hover:border-gold"}`}>
      <Poster m={m} className="w-[120px] sm:w-[150px]" />
      <p className="mt-3 text-[15px] font-bold text-ink leading-snug">{m.title}</p>
      <p className="text-[12px] text-ink-muted">{m.year}{m.directors?.length ? ` · ${m.directors[0]}` : ""}</p>
      {result && <p data-testid={`faceoff-gross-${side}`} className={`mt-2 text-[14px] font-bold ${won ? "text-gold" : "text-ink-muted"}`}>{gross(m.gross)} · #{m.rank}</p>}
      {result && picked && <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider ${won ? "text-ink" : "text-red-700"}`}>{won ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} your pick</span>}
    </button>
  );
};

/** Box Office Face-Off — 5 daily pairs, pick the bigger worldwide grosser. Streak/best in localStorage like the other quizzes. */
export const MovieFaceOff = () => {
  const dateKey = todayKey();
  const [quiz, setQuiz] = useState(null);
  const [picks, setPicks] = useState([]);
  const [result, setResult] = useState(() => getTodayResult(dateKey, KEY));
  const [state, setState] = useState(() => getQuizState(KEY));
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [board, setBoard] = useState(null);
  useEffect(() => { api.get("/movies/quiz", { params: { date: dateKey } }).then((r) => setQuiz(r.data)).catch(() => setQuiz({ pairs: [] })); }, [dateKey]);
  useEffect(() => { if (quiz && window.location.hash === "#faceoff") document.getElementById("faceoff")?.scrollIntoView({ behavior: "smooth" }); }, [quiz]);
  useEffect(() => {
    if (result) api.get("/movies/quiz/leaderboard", { params: { date: dateKey, player_id: getPlayer().id } }).then((r) => setBoard(r.data)).catch(() => {});
  }, [result, dateKey]);
  if (!quiz || quiz.pairs.length === 0) return null;

  const pick = async (id) => {
    const next = [...picks, id];
    setPicks(next);
    if (next.length < quiz.pairs.length || busy) return;
    setBusy(true);
    try {
      const player = getPlayer();
      const { data } = await api.post("/movies/quiz/answer", { date: dateKey, picks: next, player_id: player.id, player_name: player.name || undefined });
      setState(recordQuizResult(dateKey, data, KEY));
      setResult(data);
    } catch { setPicks([]); } finally { setBusy(false); }
  };
  const streak = currentStreak(dateKey, KEY);
  const i = picks.length;
  const pair = quiz.pairs[i];

  return (
    <section id="faceoff" data-testid="movie-faceoff" className="mt-8 border-2 border-ink bg-[#fbf8f1] p-4 md:p-6 scroll-mt-24">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Opening Weekend Quiz</p>
          <h2 className="mt-1 text-2xl font-bold text-ink">Box Office Face-Off</h2>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-ink-muted">
          <span data-testid="faceoff-streak" className="inline-flex items-center gap-1 px-2 py-0.5 border border-ink/20 bg-white"><Flame className="w-3 h-3 text-gold" /> Streak {streak} day{streak === 1 ? "" : "s"}</span>
          <span data-testid="faceoff-best" className="inline-flex items-center gap-1 px-2 py-0.5 border border-ink/20 bg-white"><Trophy className="w-3 h-3 text-gold" /> Best {state.bestScore || 0}</span>
        </div>
      </div>
      {!result ? (
        <>
          <p className="mt-2 text-[14px] text-ink">Which film took more at the worldwide box office? <span data-testid="faceoff-progress" className="font-semibold">{pair ? `Round ${i + 1} of ${quiz.pairs.length}` : "Checking the takings…"}</span></p>
          {pair && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-6 max-w-xl mx-auto items-stretch">
              <Contender m={pair.a} side="a" onPick={pick} />
              <Contender m={pair.b} side="b" onPick={pick} />
            </div>
          )}
          <div className="mt-4 flex justify-center gap-1.5">{quiz.pairs.map((p, k) => <span key={p.n} className={`h-1.5 w-8 rounded-full ${k < i ? "bg-gold" : "bg-ink/15"}`} />)}</div>
        </>
      ) : (
        <div data-testid="faceoff-result" className="mt-3">
          <div className="flex flex-wrap items-center gap-4">
            <span data-testid="faceoff-score" className={`px-3 py-1.5 text-[22px] font-bold ${result.total >= 80 ? "bg-gold text-ink" : "bg-white border border-ink text-ink"}`}>{result.correct}/{result.results.length}</span>
            <p className="flex-1 min-w-[200px] text-[14px] text-ink">{result.total === 100 ? "A clean sweep — you know your box office." : result.total >= 60 ? "Solid — the studios would hire you." : "Tough round — grosses are sneaky."} <span className="text-ink-muted inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {result.players_today} played today, average {result.average_today}%.</span> New pairs tomorrow.</p>
            <button type="button" data-testid="faceoff-share" onClick={() => setShareOpen(true)} className="inline-flex items-center gap-2 px-5 h-10 bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">
              <Share2 className="w-4 h-4" /> Share my score
            </button>
          </div>
          <ShareCardModal open={shareOpen} onClose={() => setShareOpen(false)} card={shareCardFor(result, streak)} shareUrl={`${window.location.origin}/movies#faceoff`} heading="Share your Face-Off score" analyticsName="faceoff_share" testPrefix="faceoff-card"
            shareText={`I scored ${result.correct}/${result.results.length} on today's Box Office Face-Off${streak > 1 ? ` (${streak}-day streak)` : ""} — can you beat me?`} />
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {result.results.map((r) => (
              <div key={r.n} data-testid={`faceoff-round-${r.n}`} className="rounded-xl border border-ink/15 bg-white/60 p-2">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-muted">Round {r.n} {r.correct ? <span className="text-gold">· correct</span> : <span className="text-red-700">· missed</span>}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Contender m={r.a} side={`a-${r.n}`} onPick={() => {}} result={r} />
                  <Contender m={r.b} side={`b-${r.n}`} onPick={() => {}} result={r} />
                </div>
              </div>
            ))}
          </div>
          <FaceOffLeaderboard board={board} onBoard={setBoard} dateKey={dateKey} />
        </div>
      )}
    </section>
  );
};
